import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { MenuItem } from '@mui/material';

import ProductSelector from '../../../../components/ContractModal/ProductSelector';
import ChallengeProgramSelector from '../../../../components/ContractModal/ChallengeProgramSelector';
import ChallengeConfigMealPlanSelector from '../../../../components/ContractModal/ChallengeConfigMealPlanSelector';
import {
  FormField,
  StyledAdornment,
  StyledTermUnitSelect,
} from '../../../../components/ContractModal/ContractConfigurator/styles';
import { UpsellOfferType } from '../../../../Model/UpsellConfig';
import { DEFAULT_CURRENCY } from '../../../../../utils/currency';
import { DURATION_UNIT } from '../../../../../utils/durationUnit';
import texts from './texts.json';
import {
  OfferRow,
  OfferSecondaryRow,
  OfferPriceSelector,
  OfferInput,
} from './styles';

const createEmptyDraftOffer = (currency) => ({
  type: UpsellOfferType.COACHING,
  currency: currency || DEFAULT_CURRENCY.code,
  productId: '',
  productName: '',
  monthlyPrice: '',
  minSubscriptionMonths: 3,
  price: '',
  duration: '',
  durationUnit: DURATION_UNIT.WEEK,
  programId: '',
  mealPlanId: '',
  challengeConfigId: '',
});

/**
 * Inline mini form defining what a touchpoint offers, composed from the
 * contract generator's own field components so both surfaces look identical.
 * A single product selector drives the offer type: challenge products become
 * one-time challenge offers (access duration plus the program and meal plan the
 * server turns into a challengeConfig at accept time), everything else a monthly
 * coaching offer.
 *
 * The currency is shown but not editable: the server bills the client on their
 * own contract and rejects an offer in any other currency, so it is derived from
 * that contract rather than chosen.
 */
const OfferForm = ({
  offer,
  onChange,
  products,
  currency,
  programs,
  mealPlans,
  hasOriginalPrice,
}) => {
  const draftOffer = offer || createEmptyDraftOffer(currency);

  const selectableProducts = products.filter((product) => (
    !product.isArchived || product.id === draftOffer.productId
  ));
  const selectedProduct = selectableProducts.find((product) => product.id === draftOffer.productId) || null;
  const isChallengeOffer = draftOffer.type === UpsellOfferType.CHALLENGE;
  const isSingleTerm = Number(isChallengeOffer ? draftOffer.duration : draftOffer.minSubscriptionMonths) === 1;

  const handleProductChange = useCallback((product) => {
    if (!product) {
      onChange(createEmptyDraftOffer(draftOffer.currency));
      return;
    }
    // The product determines the offer type; price and term fields reset on switch.
    onChange({
      ...createEmptyDraftOffer(draftOffer.currency),
      ...(product.id === draftOffer.productId ? { challengeConfigId: draftOffer.challengeConfigId } : {}),
      type: product.isChallengeProduct ? UpsellOfferType.CHALLENGE : UpsellOfferType.COACHING,
      productId: product.id,
      productName: product.name || '',
    });
  }, [onChange, draftOffer.currency, draftOffer.productId, draftOffer.challengeConfigId]);

  const handlePriceFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    onChange({ ...draftOffer, [name]: value });
  }, [onChange, draftOffer]);

  return (
    <>
      <OfferRow>
        <FormField>
          <ProductSelector
            selectedProduct={selectedProduct}
            onChange={handleProductChange}
            products={selectableProducts}
            selectId="upsell-offer-product-selector"
            label={texts.productLabel}
          />
        </FormField>
        <OfferPriceSelector
          name={isChallengeOffer ? 'price' : 'monthlyPrice'}
          currency={currency}
          value={(isChallengeOffer ? draftOffer.price : draftOffer.monthlyPrice) ?? ''}
          fieldText={isChallengeOffer ? texts.oneTimePrice : texts.monthlyPrice}
          onChange={handlePriceFieldChange}
          isCurrencyReadOnly
        />
        {hasOriginalPrice && !isChallengeOffer && (
          <OfferPriceSelector
            name="originalMonthlyPrice"
            currency={currency}
            value={draftOffer.originalMonthlyPrice ?? ''}
            fieldText={texts.originalMonthlyPrice}
            onChange={handlePriceFieldChange}
            isCurrencyReadOnly
          />
        )}
        {isChallengeOffer ? (
          <FormField>
            <OfferInput
              $hasUnitPicker
              $wideNumber
              size="small"
              name="duration"
              label={texts.durationLabel}
              value={draftOffer.duration ?? ''}
              onChange={(event) => onChange({ ...draftOffer, duration: event.target.value })}
              type="number"
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: (
                  <StyledTermUnitSelect
                    size="small"
                    value={draftOffer.durationUnit || DURATION_UNIT.WEEK}
                    onChange={(event) => onChange({ ...draftOffer, durationUnit: event.target.value })}
                    inputProps={{ 'aria-label': texts.durationUnitLabel }}
                  >
                    <MenuItem value={DURATION_UNIT.MONTH}>{isSingleTerm ? texts.unitMonth : texts.unitMonths}</MenuItem>
                    <MenuItem value={DURATION_UNIT.WEEK}>{isSingleTerm ? texts.unitWeek : texts.unitWeeks}</MenuItem>
                  </StyledTermUnitSelect>
                ),
              }}
            />
          </FormField>
        ) : (
          <FormField>
            <OfferInput
              size="small"
              name="minSubscriptionMonths"
              label={texts.commitmentLabel}
              value={draftOffer.minSubscriptionMonths ?? 3}
              onChange={(event) => onChange({ ...draftOffer, minSubscriptionMonths: event.target.value })}
              type="number"
              InputProps={{
                inputProps: { min: 1 },
                endAdornment: (
                  <StyledAdornment position="end">
                    {isSingleTerm ? texts.unitMonth : texts.unitMonths}
                  </StyledAdornment>
                ),
              }}
            />
          </FormField>
        )}
      </OfferRow>
      {isChallengeOffer && (
        <OfferSecondaryRow>
          <FormField>
            <ChallengeProgramSelector
              programs={programs}
              selectedProgramId={draftOffer.programId || ''}
              onChange={(program) => onChange({ ...draftOffer, programId: program?.id || '' })}
            />
          </FormField>
          <FormField>
            <ChallengeConfigMealPlanSelector
              mealPlans={mealPlans}
              selectedMealPlanId={draftOffer.mealPlanId || ''}
              onChange={(mealPlan) => onChange({ ...draftOffer, mealPlanId: mealPlan?.id || '' })}
            />
          </FormField>
        </OfferSecondaryRow>
      )}
    </>
  );
};

OfferForm.propTypes = {
  offer: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  currency: PropTypes.string,
  programs: PropTypes.array,
  mealPlans: PropTypes.array,
  // Offers an optional regular price for a 1:1, shown struck through beside the price charged.
  hasOriginalPrice: PropTypes.bool,
};

OfferForm.defaultProps = {
  offer: null,
  currency: DEFAULT_CURRENCY.code,
  programs: [],
  mealPlans: [],
  hasOriginalPrice: false,
};

export default OfferForm;
export { createEmptyDraftOffer };
